import {
  formatDisplayEmptyValue,
  getRowColumnValue,
} from 'components/SampleEditor/helpers/functions';
import { getExportFilename } from 'components/OrderSlidesList/BaseOrderSlidesListView';

import { NOTE_EXCEL_SAMPLES } from 'components/OrderForm/SamplesFormV2/constants';
import XLSX from 'xlsx-js-style';
import { useImportFileSamplesMutation } from 'store/apis/samplesApi';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useState } from 'react';
import {
  IMPORT_SAMPLES_FILE_ERROR_MESSAGE,
  WRONG_FILE_TYPE_ERROR_MESSAGE,
} from 'constants/errorMessages';
import { IMPORT_SAMPLES_FILE_SUCCESS_MESSAGE } from 'constants/successMessages';

export const useExportGridDataToExcel = ({ sheetName, fileName }) => {
  const exportGridDataToExcel = ({ gridAPI, onlySelected }) => {
    // do not export first empty column with checkbox
    const columns = gridAPI.columnModel.displayedColumns.slice(1);
    const uuidColumn = gridAPI.columnModel.gridColumns.find(
      (column) => column.colId === 'uuid',
    );
    const firstSheet = gridAPI.getSheetDataForExcel({
      columnGroups: true,
      shouldRowBeSkipped: (params) => !params.node.data.name,
      allColumns: false,
      sheetName,
      processCellCallback: ({ value }) => formatDisplayEmptyValue(value),
      onlySelected,
      columnKeys: [...columns, uuidColumn],
    });
    const secondSheetColumns = columns.map((column) => ({
      ...column,
      userProvidedColDef: { ...column.userProvidedColDef, cellClass: '' }, // remove styles
    }));
    const secondSheet = gridAPI.getSheetDataForExcel({
      prependContent: NOTE_EXCEL_SAMPLES,
      columnWidth: 200,
      sheetName: 'Notes',
      skipColumnHeaders: true,
      columnKeys: secondSheetColumns,
      processCellCallback: () => '',
    });

    gridAPI.exportMultipleSheetsAsExcel({
      data: [firstSheet, secondSheet],
      fileName: getExportFilename(fileName),
    });
  };

  return exportGridDataToExcel;
};

export const useImportExcelToGridTable = ({
  isSampleEditorPage,
  importGridAPIHandler,
}) => {
  const [isGridAPIUploading, setIsGridAPIUploading] = useState(false);

  const [importFileSamples, { isLoading: isAPIUploading }] =
    useImportFileSamplesMutation();
  const { showError, showSuccess } = useSnackbar();

  const importExcelViaGridAPI = (file) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      setIsGridAPIUploading(true);

      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonDataArray = XLSX.utils.sheet_to_json(worksheet, {
        range: 0, // starts from header
        defval: null,
        header: 1,
      });

      if (jsonDataArray.length > 1) {
        const columnsArray = jsonDataArray[0];
        const rowsData = jsonDataArray.slice(1).map((row) => {
          const rowData = {};

          Object.values(row).forEach((rowColumn, index) => {
            rowData[columnsArray[index]] = getRowColumnValue(rowColumn);
          });

          return rowData;
        });

        importGridAPIHandler?.(rowsData);
      }

      setIsGridAPIUploading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const importExcelViaApi = (file) => {
    const formData = new FormData();
    formData.append('file', file);

    importFileSamples(formData)
      .unwrap()
      .then(() => {
        showSuccess(IMPORT_SAMPLES_FILE_SUCCESS_MESSAGE);
      })
      .catch(() => {
        showError(IMPORT_SAMPLES_FILE_ERROR_MESSAGE);
      });
  };

  const importExcel = (file) => {
    if (isSampleEditorPage) {
      importExcelViaApi(file);
    } else {
      importExcelViaGridAPI(file);
    }
  };

  const handleSetFile = (event) => {
    const file = event.target.files[0];
    const isExcelFormat = file.name
      .split('.')
      .pop()
      .toLowerCase()
      .includes('xls');

    if (isExcelFormat) {
      importExcel(file);
    } else {
      showError(WRONG_FILE_TYPE_ERROR_MESSAGE);
    }

    event.target.value = '';
  };

  return { handleSetFile, isUploading: isGridAPIUploading || isAPIUploading };
};
